import { useState } from 'react';

import {
  numberToCurrencyString,
  dateToLabel,
  CalendarDay,
} from 'further-ui/utils';

import { makeStyles } from 'tss-react/mui';
import Table, { TableActions } from 'components/Table';
import PageContainer from 'components/PageContainer';
import { Delete, CloudDownload } from '@mui/icons-material';
import { useFilters, usePagination } from 'further-ui/hooks';
import InterestPaymentFilter from './Filters';
import downloadFile from 'utils/downloadFile';
import AlertDialog from 'components/AlertDialog';
import PageContent from 'components/PageContent';
import api from 'lib/trpcClient';
import { round } from 'lodash';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const useStyles = makeStyles()(() => ({
  tableColumnCenter: {
    minWidth: 100,
    textAlign: 'center',
  },
  tableColumnActions: {
    minWidth: 'auto',
    width: 200,
  },
}));

const InterestPaymentList: React.FC = () => {
  const { classes } = useStyles();
  const pagination = usePagination({
    orderBy: 'createdAt',
    order: 'desc',
    id: 'interest-payment',
  });
  const [canDeleteInterestPayments] = useHasPermissions([
    RolePermission.DeleteInterest,
  ]);
  const { filters, handleChangeFilters } = useFilters('interest-payment', {
    defaultFilters: {
      uploadDateRange: { startDate: null, endDate: null },
      paymentDateRange: { startDate: null, endDate: null },
    },
    onFiltersChange: pagination.toFirstPage,
  });

  const { isFetching, data } = api.interestPayment.list.useQuery({
    order: pagination.order,
    orderBy: pagination.orderBy,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    paymentStartDate: filters.paymentDateRange.startDate
      ? new CalendarDay(filters.paymentDateRange.startDate)
      : undefined,
    paymentEndDate: filters.paymentDateRange.endDate
      ? new CalendarDay(filters.paymentDateRange.endDate)
      : undefined,
    uploadStartDate: filters.uploadDateRange.startDate
      ? new CalendarDay(filters.uploadDateRange.startDate)
      : undefined,
    uploadEndDate: filters.uploadDateRange.endDate
      ? new CalendarDay(filters.uploadDateRange.endDate)
      : undefined,
  });

  const deleteInterestPaymentUpload = api.interestPayment.delete.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Interest payment deleted.',
    }),
  );

  const downloadInterestPayment = api.interestPayment.download.useMutation(
    useHandleTrpcMutation()({}),
  );

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteRecordId, setDeleteRecordId] = useState();

  const columns = [
    {
      label: 'Payment date',
      key: 'dates',
      render: (elm) =>
        elm?.dates?.length > 1 ? 'Various' : dateToLabel(elm?.dates?.[0]),
    },
    {
      label: 'Upload date',
      key: 'createdAt',
      render: (elm) => <>{dateToLabel(elm?.createdAt)}</>,
    },
    {
      label: 'Amount',
      key: 'totalInterest',
      className: classes.tableColumnCenter,
      render: (elm) => {
        const totalInterest = round(elm?.totalInterest, 2);
        return (
          <div className={classes.tableColumnCenter}>
            {numberToCurrencyString(totalInterest)}
          </div>
        );
      },
    },
    {
      label: '# Investors',
      key: 'uniqueInvestors',
      sort: false,
      className: classes.tableColumnCenter,
      render: (elm) => (
        <div className={classes.tableColumnCenter}>{elm?.uniqueInvestors}</div>
      ),
    },
    {
      label: 'Actions',
      key: '',
      sort: false,
      className: `${classes.tableColumnCenter} ${classes.tableColumnActions}`,
      render: (elm) => (
        <div className={classes.tableColumnCenter}>
          <TableActions
            showAsDropdown={true}
            actions={[
              {
                label: 'Download',
                icon: CloudDownload,
                color: 'primary',
                onClick: () => handleDownload(elm.uploadId),
              },
              {
                label: 'Delete',
                icon: Delete,
                visible: canDeleteInterestPayments,
                color: 'error',
                onClick: () => handleDeleteWithConfirm(elm.uploadId),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  // table row action handlers
  const handleDeleteWithConfirm = (uploadId) => {
    setDeleteRecordId(openConfirmDialog ? null : uploadId);
    setOpenConfirmDialog(!openConfirmDialog);
  };

  const handleConfirmDelete = () => {
    if (deleteRecordId) {
      deleteInterestPaymentUpload.mutate({ uploadId: deleteRecordId });
    }
    setOpenConfirmDialog(false);
  };

  const handleDownload = async (uploadId: string) => {
    const { mimeType, content, name } =
      await downloadInterestPayment.mutateAsync({
        uploadId,
      });
    const uint8 = Uint8Array.from(atob(content), (c) => c.charCodeAt(0));
    const blob = new Blob([uint8], { type: mimeType });
    downloadFile(blob, name);
  };

  return (
    <PageContainer heading="Interest: Uploads summary">
      <PageContent>
        <InterestPaymentFilter
          filters={filters}
          handleChangeFilters={handleChangeFilters}
        />
        <Table
          columns={columns}
          onRequestSort={pagination.handleRequestSort}
          order={pagination.order}
          orderBy={pagination.orderBy}
          tablebody={data?.result ?? []}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          count={data?.total ?? 0}
          pagination={true}
          loading={isFetching}
          variant="nohover"
        />
      </PageContent>

      <AlertDialog
        open={openConfirmDialog}
        title="Confirm delete"
        content={
          'Are you sure you want to remove this upload and all its associated records?'
        }
        onClose={handleDeleteWithConfirm}
        onConfirm={handleConfirmDelete}
      />
    </PageContainer>
  );
};

export default InterestPaymentList;
